import React, { Fragment, useMemo } from 'react';

import { Accordion, Box, Text } from '@opendoor/bricks-next';
import type { ListingHistoryState } from '__generated__/athena';
import { Divider } from 'components/shared/Divider';
import { formatCurrency } from 'helpers/formatters';

interface MarketplaceListingHistory {
  state?: ListingHistoryState | null;
  timestamp?: any | null;
  mls?: string | null;
  originalListPriceCents?: number | null;
  soldPriceCents?: number | null;
  currentListPriceCents?: number | null;
}

interface PriceHistoryProps {
  listingHistory: MarketplaceListingHistory[];
}

export const PriceHistory: React.FC<PriceHistoryProps> = ({ listingHistory }) => {
  // Dedupe by date after cleaning up timestamp format
  const listingHistoryByDateMap: Record<string, MarketplaceListingHistory> =
    listingHistory?.reduce(
      (map: Record<string, MarketplaceListingHistory>, history: MarketplaceListingHistory) => {
        // ex: January 10, 2023
        const date = new Date(history.timestamp).toLocaleDateString('en-us', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        });
        if (!map[date]) {
          map[date] = { ...history, timestamp: date };
        }

        return map;
      },
      {},
    ) || {};

  // Filter out events without any pricing data, and any marketplace specific events
  const pricingEvents = Object.values(listingHistoryByDateMap)
    .filter(
      (history) =>
        history.state !== 'PRIVATELY_LISTED' && !(history.state === 'SALE_PENDING' && !history.mls),
    )
    .filter(
      (history) =>
        history?.soldPriceCents ||
        history?.currentListPriceCents ||
        history?.originalListPriceCents,
    );
  const description = useMemo(() => {
    const rows = Object.values(pricingEvents)?.map((history: MarketplaceListingHistory, index) => {
      const state = history.state as ListingHistoryState;
      const priceCents =
        state === 'SOLD' || state === 'OFF_MARKET'
          ? history?.soldPriceCents || history?.currentListPriceCents || 0
          : history?.currentListPriceCents || history?.originalListPriceCents || 0;

      return (
        <Fragment key={history.timestamp}>
          <Box
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            mb={index === pricingEvents.length - 1 ? 24 : 0}
          >
            <Box flexDirection="column">
              <Text tag="p" typography="$bodySmall" color="$contentPrimary">
                {history.timestamp}
              </Text>
              <Text
                tag="span"
                typography="$bodyXsmall"
                color="$contentSecondary"
                textTransform="capitalize"
              >
                {state?.split('_').join(' ').toLowerCase()}
                {history.mls && ` · ${history.mls}`}
              </Text>
            </Box>
            <Text tag="strong" typography="$labelSmall" color="$contentPrimary">
              {formatCurrency(priceCents)}
            </Text>
          </Box>
          {index !== pricingEvents.length - 1 && <Divider className="py-4" />}
        </Fragment>
      );
    });

    if (!rows.length) {
      return 'No price history';
    }

    return rows;
  }, [pricingEvents]);

  return (
    <>
      <Box
        $smallerThanLG={{ display: 'none' }}
        data-testid="marketplace-price-history-accordion-desktop"
      >
        <Accordion
          items={[
            {
              title: 'Price history',
              description,
            },
          ]}
          variant="medium"
          size="xlarge"
          isFullWidth
          defaultOpen
        />
      </Box>
      <Box
        $largerThanMD={{ display: 'none' }}
        data-testid="marketplace-price-history-accordion-mobile"
      >
        <Accordion
          items={[
            {
              title: 'Price history',
              description,
            },
          ]}
          variant="medium"
          size="large"
          isFullWidth
          defaultOpen
        />
      </Box>
    </>
  );
};
